<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { location } from 'svelte-spa-router';
    import type { User, UpdateProfileData } from '../lib/api/auth.service';
    import { AuthService } from '../lib/api/auth.service';
    import { userStore } from '../stores/auth.store';
    import Input from './Input.svelte';
    import Notification from './Notification.svelte';
    import DonationRequestModal from './employee/myDonations/DonationRequestModal.svelte';
    import { EmployeeEditProfile, EmployerEditProfile, CharityEditProfile } from './edit-profile';
    import { auth } from '../stores/auth.store';
    import Button from './Button.svelte';
    import {
      charityPendingConfirmation,
    } from '../stores/charity-pending-confirmation.store';
    import {
      employerPendingActions,
    } from '../stores/employer-pending-actions.store';
    
    export let pageTitle: string = 'Welcome Back';
    export let user: User | null = null;
    export let mobileNavEnabled = false;
    export let mobileSidebarOpen = false;

    const dispatch = createEventDispatcher<{ toggleSidebar: void }>();
  
    let showUserPopup = false;
    let popupElement: HTMLElement;
    let profileContainer: HTMLElement;
    
    // Email change modal state
    let showEmailModal = false;
    let emailLoading = false;
    let emailData = {
      email: ''
    };
    
    // Settings modal state
    let showSettingsModal = false;
    
    // Notification state
    let notificationShow = false;
    let notificationType: 'success' | 'error' = 'success';
    let notificationMessage = '';
    function showNotification(message: string, type: 'success' | 'error' = 'success') {
      notificationMessage = message;
      notificationType = type;
      notificationShow = true;
    }
  
    $: displayName = userRole === 'charity' || userRole === 'employer' ? user?.organization?.data?.name : user?.full_name || 'User';
    $: showUserNameInTitle = pageTitle === 'Welcome Back';
    $: userRole = user?.user_role?.name?.toLowerCase() || 'user'; // Get the user role
    $: charityBankVerified =
      userRole === 'charity' && user?.organization?.data?.bank_details_status === 'verified';
    
    // Dynamic search placeholder based on the current page
    $: searchPlaceholder = getSearchPlaceholder($location);
    
    function getSearchPlaceholder(location: string): string {
      if (location.includes('/pending-confirmation')) {
        return 'Search pending confirmations';
      } else if (location.includes('/payouts')) {
        return 'Search payouts';
      } else if (location.includes('/charities')) {
        return 'Search charities';
      } else if (location.includes('/donors')) {
        return 'Search donors';
      } else if (location.includes('/transactions')) {
        return 'Search transactions';
      } else {
        return 'Search';
      }
    }

    function toggleUserPopup(event: Event) {
      event.stopPropagation();
      showUserPopup = !showUserPopup;
    }

    function closeUserPopup() {
      showUserPopup = false;
    }
    
    function openEmailModal() {
      // Initialize with current user email
      emailData = {
        email: ''
      };
      showEmailModal = true;
      closeUserPopup();
    }
    
    function closeEmailModal() {
      showEmailModal = false;
    }
    
    function openSettingsModal() {
      showSettingsModal = true;
      closeUserPopup();
    }
    
    function closeSettingsModal() {
      showSettingsModal = false;
    }
    
    async function handleEmailUpdate() {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailData.email.trim()) {
        showNotification('Email is required', 'error');
        return;
      }
      
      if (!emailRegex.test(emailData.email)) {
        showNotification('Please enter a valid email address', 'error');
        return;
      }
      
      emailLoading = true;
      try {
        const result = await AuthService.updateEmail({
          email: emailData.email
        });
        
        if (result.success) {
          showNotification('Email sent successfully! Please check your email address for verification link.', 'success');
          closeEmailModal();
        } else {
          showNotification((result as any)?.error?.response?.data?.data?.errors?.email || 'Failed to send email. Please try again.', 'error');
        }
      } catch (error: any) {
        
        // Handle specific error messages
        if (error?.response?.data?.message) {
          showNotification(error.response.data.message, 'error');
        } else if (error?.response?.data?.errors) {
          const errors = error.response.data.errors;
          const errorMessages = Object.values(errors).flat().join(', ');
          showNotification(errorMessages, 'error');
        } else {
          showNotification('Failed to send email. Please try again.', 'error');
        }
      } finally {
        emailLoading = false;
      }
    }
    
    // Handle profile update success
    function handleProfileUpdateSuccess(event: CustomEvent) {
      const { message, user: updatedUser } = event.detail;
      userStore.set(updatedUser);
      user = updatedUser;
      showNotification(message || 'Profile updated successfully!', 'success');
      closeSettingsModal();
    }
    
    // Handle profile update error
    function handleProfileUpdateError(event: CustomEvent) {
      const { message } = event.detail;
      showNotification(message || 'Failed to update profile. Please try again.', 'error');
    }

    async function handleLogout() {
      try {
        await auth.logout();
      } catch (error) {
        showNotification('Failed to logout. Please try again.', 'error');
      }
    }

    function handleClickOutside(event: MouseEvent) {
      if (showUserPopup && profileContainer && !profileContainer.contains(event.target as Node)) {
        closeUserPopup();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (showSettingsModal) {
          closeSettingsModal();
        } else if (showEmailModal) {
          closeEmailModal();
        } else if (showUserPopup) {
          closeUserPopup();
        }
      }
    }
    
    function handleEmailModalKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter' && !emailLoading) {
        handleEmailUpdate();
      }
    }

    // Donation modal state
    let showDonationModal = false;

    function handleNewDonationRequest() {
      showDonationModal = true;
    }

    function handleModalClose() {
      showDonationModal = false;
    }

    function handleDonationComplete() {
      showDonationModal = false;
      showNotification('Donation request submitted successfully!', 'success');
    }
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleEscape} />

<div class="topbar">
  <div class="welcome">
    {#if mobileNavEnabled}
      <button
        type="button"
        class="menu-toggle"
        aria-label={mobileSidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={mobileSidebarOpen}
        on:click={() => dispatch('toggleSidebar')}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
        </svg>
      </button>
    {/if}
    <h2>{pageTitle}{showUserNameInTitle ? `, ${displayName}` : ''}</h2>
  </div>
  
  <!-- <div class="search">
    <div class="search-container">
      <input type="text" placeholder={searchPlaceholder} on:input={(e) => onSearch(e.currentTarget.value)} />
      <button type="button" class="search-button">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
        </svg>
      </button>
    </div>
  </div> -->
  
  <div class="user-actions">
    {#if userRole === 'charity' && $charityPendingConfirmation.count > 0}
      <button
        type="button"
        class="pending-confirm-alert"
        on:click={() => push('/pending-confirmation')}
        aria-label="{$charityPendingConfirmation.count} payment{$charityPendingConfirmation.count === 1 ? '' : 's'} awaiting confirmation"
      >
        <span class="pending-confirm-dot" aria-hidden="true"></span>
        <span class="pending-confirm-text">
          {$charityPendingConfirmation.count} to confirm
        </span>
      </button>
    {/if}

    {#if userRole === 'employer' && $employerPendingActions.pendingRequestsCount > 0}
      <button
        type="button"
        class="employer-pending-alert employer-pending-requests-alert"
        on:click={() => push('/leave-requests')}
        aria-label="{$employerPendingActions.pendingRequestsCount} pending request{$employerPendingActions.pendingRequestsCount === 1 ? '' : 's'}"
      >
        <span class="employer-pending-dot employer-pending-requests-dot" aria-hidden="true"></span>
        <span class="employer-pending-text">
          {$employerPendingActions.pendingRequestsCount} to review
        </span>
      </button>
    {/if}

    {#if userRole === 'employer' && ($employerPendingActions.pendingPaymentsCount > 0 || $employerPendingActions.pendingPaymentsValue > 0)}
      <button
        type="button"
        class="employer-pending-alert employer-pending-payments-alert"
        on:click={() => push('/employer/outstanding-invoices')}
        aria-label="{$employerPendingActions.pendingPaymentsCount} pending payment{$employerPendingActions.pendingPaymentsCount === 1 ? '' : 's'}"
      >
        <span class="employer-pending-dot employer-pending-payments-dot" aria-hidden="true"></span>
        <span class="employer-pending-text">
          {$employerPendingActions.pendingPaymentsCount} to pay
        </span>
      </button>
    {/if}

    {#if userRole === 'user'}
      <Button variant="primary" on:click={handleNewDonationRequest} class="donate-button">
        <span class="button-content">
          <span class="button-text">New Donation Request</span>
        </span>
      </Button>
    {/if}
    
    <div class="profile-container" bind:this={profileContainer}>
      <div class="profile-pic" on:click={toggleUserPopup} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && toggleUserPopup(e)}>
        <img src={user?.profile_pic || 'https://ui-avatars.com/api/?name=' + displayName} alt="Profile" />
      </div>
      
      {#if showUserPopup}
        <div 
          class="user-popup" 
          bind:this={popupElement} 
          role="dialog"
          aria-label="User menu"
        >
          <div class="popup-header">
            <div class="popup-profile-pic">
              <img src={user?.profile_pic || 'https://ui-avatars.com/api/?name=' + displayName} alt="Profile" />
            </div>
            <h3>{displayName}</h3>
            <button class="close-button" on:click={closeUserPopup}>×</button>
          </div>
          
          <div class="popup-content">
            <div class="detail-item">
              <span class="label">Email:</span>
              <span class="value">{user?.email || 'Not provided'}</span>
            </div>
            
            {#if user?.phone}
              <div class="detail-item">
                <span class="label">Phone:</span>
                <span class="value">{ userRole === 'charity' || userRole === 'employer' ? user.organization.data.contact_phone : user.phone}</span>
              </div>
            {/if}
            
            {#if user?.user_role}
              <div class="detail-item">
                <span class="label">Role:</span>
                <span class="value role-badge">{user.user_role.name}</span>
              </div>
            {/if}

            {#if userRole === 'charity' && user?.organization?.data}
              <div class="detail-item">
                <span class="label">Bank status:</span>
                <span
                  class="value bank-status-pill"
                  class:bank-status-verified={charityBankVerified}
                  class:bank-status-unverified={!charityBankVerified}
                >
                  {charityBankVerified ? 'Verified' : 'Unverified'}
                </span>
              </div>
            {/if}
            
            <div class="popup-actions">
              {#if userRole !== 'charity'}
                <button class="profile-btn" on:click={() => { closeUserPopup(); push(userRole === 'employer' ? '/employer-profile' : '/profile'); }}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  {userRole === 'employer' ? 'My Employer Profile' : 'My Profile'}
                </button>
              {/if}
              <button class="logout-btn" on:click={handleLogout}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>

<!-- Email Change Modal -->
{#if showEmailModal}
  <div 
    class="modal-overlay" 
    role="presentation"
    on:click|self={closeEmailModal}
    on:keydown={(e) => e.key === 'Escape' && closeEmailModal()}
  >
    <div 
      class="modal-content" 
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
    >
      <div class="modal-header">
        <h3 id="email-modal-title">Send Email Verification</h3>
        <button class="modal-close" on:click={closeEmailModal}>×</button>
      </div>
      
      <form class="modal-body" on:submit|preventDefault={handleEmailUpdate}>
        <div class="form-group">
          <Input 
            type="email"
            label="Email"
            bind:value={emailData.email}
            placeholder="Enter your email address"
            required={true}
            fullWidth={true}
          />
        </div>
        
        <p class="info-text">
          📧 An email will be sent to the specified email address with a verification link to update your email.
        </p>

        <div class="modal-footer">
          <button type="button" class="btn-secondary" on:click={closeEmailModal} disabled={emailLoading}>
            Cancel
          </button>
          <button type="submit" class="btn-primary" disabled={emailLoading || !emailData.email.trim()}>
            {emailLoading ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Role-specific Settings Modal -->
{#if userRole === 'user'}
  <EmployeeEditProfile 
    isOpen={showSettingsModal}
    {user} 
    on:close={closeSettingsModal}
    on:success={handleProfileUpdateSuccess}
    on:error={handleProfileUpdateError}
  />
{:else if userRole === 'charity'}
  <CharityEditProfile 
    isOpen={showSettingsModal}
    {user} 
    on:close={closeSettingsModal}
    on:success={handleProfileUpdateSuccess}
    on:error={handleProfileUpdateError}
  />
{:else}
  <!-- Default fallback for admin or unknown roles -->
  <EmployeeEditProfile 
    isOpen={showSettingsModal}
    {user} 
    on:close={closeSettingsModal}
    on:success={handleProfileUpdateSuccess}
    on:error={handleProfileUpdateError}
  />
{/if}

<!-- Notification Component -->
<Notification 
  show={notificationShow} 
  type={notificationType} 
  message={notificationMessage} 
  onDismiss={() => { notificationShow = false; }}
/>

<!-- Donation Request Modal -->
<DonationRequestModal 
  isOpen={showDonationModal}
  on:close={handleModalClose}
  on:complete={handleDonationComplete}
/>

<style>
  .topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 2rem;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    height: 70px;
    border-bottom: 1px solid #e5e7eb;
    z-index: 10;
    position: sticky;
    top: 0;
  }
  
  .welcome {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    flex: 1;
  }

  .menu-toggle {
    display: none;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: #374151;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 8px;
    min-width: 44px;
    min-height: 44px;
    flex-shrink: 0;
    transition: background-color 0.2s ease;
  }

  .menu-toggle:hover {
    background-color: #f3f4f6;
  }

  .menu-toggle:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .welcome h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .user-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
  }

  .pending-confirm-alert {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    border: 1px solid #c4b5fd;
    border-radius: 9999px;
    background: #f5f3ff;
    color: #5b21b6;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    white-space: nowrap;
  }

  .pending-confirm-alert:hover {
    background: #ede9fe;
    border-color: #a78bfa;
  }

  .pending-confirm-alert:focus {
    outline: none;
  }

  .pending-confirm-alert:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .pending-confirm-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #8b5cf6;
    flex-shrink: 0;
  }

  .employer-pending-alert {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    white-space: nowrap;
  }

  .employer-pending-alert:focus {
    outline: none;
  }

  .employer-pending-alert:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .employer-pending-requests-alert {
    border: 1px solid #fcd34d;
    background: #fffbeb;
    color: #92400e;
  }

  .employer-pending-requests-alert:hover {
    background: #fef3c7;
    border-color: #fbbf24;
  }

  .employer-pending-payments-alert {
    border: 1px solid #fca5a5;
    background: #fef2f2;
    color: #991b1b;
  }

  .employer-pending-payments-alert:hover {
    background: #fee2e2;
    border-color: #f87171;
  }

  .employer-pending-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .employer-pending-requests-dot {
    background: #f59e0b;
  }

  .employer-pending-payments-dot {
    background: #ef4444;
  }
  
  .profile-container {
    position: relative;
  }
  
  .profile-pic {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #e2e8f0;
    cursor: pointer;
    transition: border-color 0.2s ease;
  }
  
  .profile-pic:hover {
    border-color: #f0b429;
  }
  
  .profile-pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .user-popup {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.5rem;
    width: 300px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    overflow: hidden;
    animation: slideIn 0.2s ease-out;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .popup-header {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: linear-gradient(135deg, #f0b429 0%, #f59e0b 100%);
    color: white;
    position: relative;
  }
  
  .popup-profile-pic {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid white;
    margin-right: 0.75rem;
    flex-shrink: 0;
  }
  
  .popup-profile-pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .popup-header h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    flex: 1;
  }
  
  .close-button {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }
  
  .close-button:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  .popup-content {
    padding: 1rem;
  }
  
  .detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f7fafc;
  }
  
  .detail-item:last-child {
    border-bottom: none;
  }
  
  .label {
    font-weight: 500;
    color: #4a5568;
    font-size: 0.875rem;
  }
  
  .value {
    font-size: 0.875rem;
    color: #2d3748;
    text-align: right;
  }
  
  .role-badge {
    background: #e2e8f0;
    color: #4a5568;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: capitalize;
  }

  .bank-status-pill {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .bank-status-pill.bank-status-verified {
    background: #d1fae5;
    color: #065f46;
  }

  .bank-status-pill.bank-status-unverified {
    background: #fef3c7;
    color: #92400e;
  }
  
  .popup-actions {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #f7fafc;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }

  .profile-btn,
  .logout-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: 25rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
  }

  .profile-btn:active:not(:disabled),
  .logout-btn:active:not(:disabled) {
    transform: translateY(1px);
  }

  .profile-btn {
    background-color: #f3f4f6;
    color: #374151;
  }

  .profile-btn:hover:not(:disabled) {
    background-color: #e5e7eb;
  }

  .logout-btn {
    background-color: #fee2e2;
    color: #991b1b;
  }

  .logout-btn:hover:not(:disabled) {
    background-color: #fecaca;
  }

  .profile-btn:focus-visible,
  .logout-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.25);
  }

  .profile-btn svg,
  .logout-btn svg {
    flex-shrink: 0;
  }

  /* Email Change Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    width: 90%;
    max-width: 450px;
    max-height: 90%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalFadeIn 0.3s ease-out;
  }

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(135deg, #f0b429 0%, #f59e0b 100%);
    color: white;
    position: relative;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .modal-close:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .modal-body {
    padding: 1.5rem;
    overflow-y: auto;
    flex-grow: 1;
  }

     .form-group {
     margin-bottom: 1.25rem;
   }
   
   .info-text {
     margin: 1rem 0;
     padding: 0.75rem 1rem;
     background-color: #f0f9ff;
     border: 1px solid #bae6fd;
     border-radius: 8px;
     color: #0369a1;
     font-size: 0.875rem;
     line-height: 1.4;
   }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    background-color: #f7fafc;
    border-top: 1px solid #e2e8f0;
    border-radius: 0 0 10px 10px;
  }

  .btn-secondary,
  .btn-primary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s ease, opacity 0.2s ease;
  }

  .btn-secondary {
    background-color: #edf2f7;
    color: #4a5568;
    border: 1px solid #e2e8f0;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #e2e8f0;
  }

  .btn-primary {
    background: linear-gradient(135deg, #f0b429 0%, #f59e0b 100%);
    color: white;
    border: none;
  }

  .btn-primary:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  :global(.donate-button .button-content) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    height: 100%;
  }

  :global(.donate-button svg) {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  :global(.donate-button .button-text) {
    line-height: 1;
  }

  @media (max-width: 768px) {
    .topbar {
      padding: 1rem;
      height: auto;
      min-height: 70px;
    }

    .menu-toggle {
      display: flex;
    }

    .welcome h2 {
      font-size: 1rem;
    }

    .user-actions {
      gap: 0.5rem;
      flex-shrink: 0;
    }

    :global(.donate-button) {
      padding: 0 0.75rem;
    }

    :global(.donate-button .button-text) {
      display: none;
    }

    :global(.donate-button .button-content) {
      gap: 0;
    }
  }
</style>